// Quản lý NHÂN VIÊN (employees) — M2 refactor 2026-04-22.
// NV = người thật. L2 TOTP gắn TRÊN employee (không phải user).
// Admin KHÔNG gen QR L2 hộ NV — NV tự setup lần đầu trên máy kiosk.
// Admin chỉ: tạo record NV, reset L2 (wipe secret → NV setup lại lần sau vào kiosk),
//            mở khoá, disable/enable, đổi shop, HARD DELETE (M2R.6+ Phương án C).
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { api } from '../lib/api.js';
import { showConfirm } from '../lib/dialogs.js';

export default function AdminEmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterShop, setFilterShop] = useState(''); // '' = all
  const [filterStatus, setFilterStatus] = useState('all'); // all | active | disabled
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleting, setDeleting] = useState(null); // NV object — mở HardDeleteModal

  async function reload() {
    setLoading(true);
    try {
      const [{ employees }, { shops }] = await Promise.all([
        api.adminEmployees(filterShop || undefined),
        api.adminShops(),
      ]);
      setEmployees(employees);
      setShops(shops);
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { reload(); }, [filterShop]);

  const shopById = (id) => shops.find(s => s.id === id);
  const shopLabel = (id) => {
    const s = shopById(id);
    return s ? `${s.code} — ${s.name}` : `#${id}`;
  };

  const visible = useMemo(() => {
    if (filterStatus === 'all') return employees;
    return employees.filter(e => e.status === filterStatus);
  }, [employees, filterStatus]);

  // M173 2026-05-19: group NV theo shop để admin scan nhanh theo cửa hàng.
  // Output: [[shop_id, [employees sorted by full_name]], ...] sorted theo shop.code.
  const groupedByShop = useMemo(() => {
    const map = new Map();
    for (const e of visible) {
      const key = e.shop_id ?? 0;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    }
    // Sort NV trong mỗi group theo họ tên (locale VN)
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.full_name || '').localeCompare(b.full_name || '', 'vi'));
    }
    // Sort group theo shop.code (hp trước hn theo alphabet)
    return Array.from(map.entries()).sort((a, b) => {
      const sa = shops.find(s => s.id === a[0]);
      const sb = shops.find(s => s.id === b[0]);
      return (sa?.code || 'zzz').localeCompare(sb?.code || 'zzz');
    });
  }, [visible, shops]);

  async function act(fn, msg) {
    try { await fn(); toast.success(msg); reload(); }
    catch (err) { toast.error(err.data?.error || err.message); }
  }

  return (
    <div className="min-h-full p-4 md:p-6">
      <div className="max-w-6xl mx-auto">
        <Link to="/admin" className="text-sm text-slate-500 hover:text-slate-700">← Trang admin</Link>
        <div className="flex items-start justify-between gap-3 mt-2 flex-wrap">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-brand-700">Quản lý Nhân viên</h1>
            <p className="text-xs text-slate-500 mt-1">
              NV tự setup TOTP L2 lần đầu trên máy kiosk của shop. Admin chỉ tạo record + reset khi cần.
            </p>
          </div>
          <button onClick={() => setShowCreate(true)} className="tap-feedback px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium shadow-sm w-full sm:w-auto">
            + Thêm NV
          </button>
        </div>

        <div className="mt-3 flex flex-wrap gap-2 items-center">
          <label className="text-xs text-slate-600">Shop:
            <select value={filterShop} onChange={(e) => setFilterShop(e.target.value)} className="ml-1 px-2 py-1 rounded border border-slate-300 text-sm">
              <option value="">Tất cả</option>
              {shops.map(s => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
            </select>
          </label>
          <label className="text-xs text-slate-600">Trạng thái:
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="ml-1 px-2 py-1 rounded border border-slate-300 text-sm">
              <option value="all">Tất cả</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
          <span className="text-xs text-slate-500 ml-auto">{visible.length} NV</span>
        </div>

        <div className="mt-3 bg-white rounded-2xl shadow-sm border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500">
              <tr>
                {/* M148b: hide ID + SĐT + Khoá mobile (ít quan trọng) — giữ Họ tên, Shop, L2, Status, Thao tác */}
                <th className="px-3 py-2 hidden md:table-cell">ID</th>
                <th className="px-3 py-2">Họ tên</th>
                <th className="px-3 py-2 hidden md:table-cell">SĐT</th>
                <th className="px-3 py-2">Shop</th>
                <th className="px-3 py-2">L2</th>
                <th className="px-3 py-2 hidden md:table-cell">Khoá</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Thao tác</th>
              </tr>
            </thead>
            {loading ? (
              <tbody>
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Đang tải…</td></tr>
              </tbody>
            ) : visible.length === 0 ? (
              <tbody>
                <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-400">Chưa có NV nào khớp bộ lọc</td></tr>
              </tbody>
            ) : groupedByShop.map(([shopId, emps]) => (
              <tbody key={shopId}>
                {/* M173: header row group theo shop */}
                <tr className="bg-brand-50 border-t-2 border-brand-200">
                  <td colSpan={8} className="px-3 py-2 text-sm font-semibold text-brand-700">
                    🏪 {shopLabel(shopId)}
                    <span className="ml-2 text-xs text-slate-500 font-normal">({emps.length} NV)</span>
                  </td>
                </tr>
                {emps.map(e => (
                  <tr key={e.id} className="border-t border-slate-100 align-top">
                    <td className="px-3 py-2 font-mono text-xs hidden md:table-cell">{e.id}</td>
                    <td className="px-3 py-2 font-medium">{e.full_name}</td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">{e.phone || <span className="text-slate-400">—</span>}</td>
                    <td className="px-3 py-2 text-xs">{shopLabel(e.shop_id)}</td>
                    <td className="px-3 py-2">
                      {e.otp_l2_enabled
                        ? <span className="text-xs text-emerald-700 bg-emerald-50 rounded px-2 py-0.5">✅ Đã setup</span>
                        : <span className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-0.5">⏳ Chưa setup</span>}
                    </td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">
                      {!!(e.locked_until && new Date(e.locked_until) > new Date())
                        ? <span className="text-rose-700 bg-rose-50 rounded px-1.5 py-0.5">🔒 {new Date(e.locked_until).toLocaleTimeString('vi-VN')}</span>
                        : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded ${e.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}`}>
                        {e.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex flex-wrap gap-1 justify-end">
                        <button onClick={() => setEditing(e)} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 hover:bg-slate-200">Sửa</button>
                        {e.otp_l2_enabled && (
                          <button
                            onClick={async () => {
                              const ok = await showConfirm({
                                title: 'Reset L2',
                                message: `Reset L2 của NV "${e.full_name}"?\nNV sẽ phải setup lại TOTP lần tới vào kiosk.`,
                                confirmText: 'Reset L2',
                                dangerous: true,
                              });
                              if (!ok) return;
                              act(() => api.adminResetEmployeeL2(e.id), 'Đã reset L2');
                            }}
                            className="text-xs px-2 py-1 rounded bg-sky-100 text-sky-700 hover:bg-sky-200"
                          >Reset L2</button>
                        )}
                        {!!(e.locked_until && new Date(e.locked_until) > new Date()) && (
                          <button onClick={() => act(() => api.adminUnlockEmployee(e.id), 'Đã mở khoá')} className="text-xs px-2 py-1 rounded bg-rose-100 text-rose-700 hover:bg-rose-200">Mở khoá</button>
                        )}
                        {e.status === 'active' ? (
                          <button
                            onClick={async () => {
                              const ok = await showConfirm({
                                title: 'Vô hiệu hoá NV',
                                message: `Vô hiệu hoá NV "${e.full_name}"?\nMọi phiên L2 đang mở sẽ bị thu hồi.`,
                                confirmText: 'Vô hiệu hoá',
                                dangerous: true,
                              });
                              if (!ok) return;
                              act(() => api.adminDisableEmployee(e.id), 'Đã disable');
                            }}
                            className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200"
                          >Disable</button>
                        ) : (
                          <>
                            <button onClick={() => act(() => api.adminEnableEmployee(e.id), 'Đã enable')} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Enable</button>
                            <button
                              onClick={() => setDeleting(e)}
                              className="text-xs px-2 py-1 rounded bg-rose-600 text-white hover:bg-rose-700"
                              data-testid={`delete-employee-${e.id}`}
                              title="Xoá vĩnh viễn khỏi hệ thống (không phục hồi được)"
                            >Xoá vĩnh viễn</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>

        {showCreate && (
          <EmployeeEditModal
            mode="create"
            shops={shops}
            onClose={() => setShowCreate(false)}
            onSaved={() => { setShowCreate(false); reload(); }}
          />
        )}
        {editing && (
          <EmployeeEditModal
            mode="edit"
            shops={shops}
            initial={editing}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); reload(); }}
          />
        )}
        {deleting && (
          <HardDeleteEmployeeModal
            employee={deleting}
            onClose={() => setDeleting(null)}
            onDeleted={() => { setDeleting(null); reload(); }}
          />
        )}
      </div>
    </div>
  );
}

function EmployeeEditModal({ mode, shops, initial, onClose, onSaved }) {
  const activeShops = (shops || []).filter(s => s.status === 'active');
  const [form, setForm] = useState({
    shop_id: initial?.shop_id ?? activeShops[0]?.id ?? null,
    full_name: initial?.full_name || '',
    phone: initial?.phone || '',
  });
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        shop_id: Number(form.shop_id),
        full_name: form.full_name.trim(),
        phone: form.phone.trim() || null,
      };
      if (mode === 'create') {
        await api.adminCreateEmployee(body);
        toast.success('Đã tạo NV — nhờ NV ra máy kiosk của shop để tự setup TOTP L2');
      } else {
        // edit: gửi patch chỉ field đã thay đổi
        const patch = {};
        if (body.full_name !== initial.full_name) patch.full_name = body.full_name;
        if (body.phone !== (initial.phone || null)) patch.phone = body.phone;
        if (body.shop_id !== initial.shop_id) patch.shop_id = body.shop_id;
        if (Object.keys(patch).length === 0) {
          toast('Không có thay đổi');
          onClose();
          return;
        }
        await api.adminUpdateEmployee(initial.id, patch);
        toast.success('Đã cập nhật');
      }
      onSaved();
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  const update = (k) => (e) => {
    const v = e.target.value;
    setForm(f => ({ ...f, [k]: v }));
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-20 p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-3">
        <h3 className="font-semibold text-lg">{mode === 'create' ? 'Thêm NV mới' : `Sửa NV: ${initial.full_name}`}</h3>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700">Họ tên</span>
          <input required value={form.full_name} onChange={update('full_name')} className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700">SĐT (tuỳ chọn, ví dụ 0901234567)</span>
          <input value={form.phone} onChange={update('phone')} placeholder="0901234567" className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-100 outline-none" />
        </label>
        <label className="block">
          <span className="block text-sm font-medium text-slate-700">Shop</span>
          <select
            required
            value={form.shop_id ?? ''}
            onChange={(e) => setForm(f => ({ ...f, shop_id: e.target.value ? Number(e.target.value) : null }))}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300"
          >
            {activeShops.length === 0 && <option value="">(chưa có shop active)</option>}
            {activeShops.map(s => <option key={s.id} value={s.id}>{s.code} — {s.name}</option>)}
          </select>
        </label>
        {mode === 'create' && (
          <div className="text-xs text-amber-700 bg-amber-50 rounded p-2 border border-amber-200">
            Sau khi tạo: nhờ NV ra <strong>máy kiosk của shop</strong>, bấm tên mình → setup TOTP lần đầu bằng app Authenticator trên điện thoại cá nhân của NV.
          </div>
        )}
        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium">Huỷ</button>
          <button type="submit" disabled={busy || !form.shop_id} className="flex-1 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium disabled:opacity-50">
            {busy ? 'Đang lưu…' : (mode === 'create' ? 'Tạo' : 'Lưu')}
          </button>
        </div>
      </form>
    </div>
  );
}

// ===================================================================
// HardDeleteEmployeeModal — Phương án C
// - Chỉ mở được khi NV ở status='disabled' (button ngoài gating)
// - Yêu cầu: nhập chính xác full_name + mã TOTP L1 6 chữ số
// - Thao tác không phục hồi → cảnh báo mạnh, nút đỏ, 2 bước
// ===================================================================
function HardDeleteEmployeeModal({ employee, onClose, onDeleted }) {
  const [nameConfirm, setNameConfirm] = useState('');
  const [totp, setTotp] = useState('');
  const [busy, setBusy] = useState(false);
  const nameMatch = nameConfirm.trim() === (employee?.full_name || '');
  const totpOk = /^\d{6}$/.test(totp.trim());

  async function submit(e) {
    e.preventDefault();
    if (!nameMatch || !totpOk) return;
    setBusy(true);
    try {
      await api.adminHardDeleteEmployee({
        employeeId: employee.id,
        fullNameConfirm: nameConfirm.trim(),
        totpCode: totp.trim(),
      });
      toast.success(`Đã xoá vĩnh viễn NV "${employee.full_name}"`);
      onDeleted();
    } catch (err) {
      toast.error(err.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-30 p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-3 border-2 border-rose-300">
        <div className="flex items-start gap-3">
          <div className="text-3xl">⚠️</div>
          <div>
            <h3 className="font-semibold text-lg text-rose-700">Xoá vĩnh viễn nhân viên</h3>
            <p className="text-xs text-slate-600 mt-0.5">
              Hành động này <strong>không thể hoàn tác</strong>. Toàn bộ session, lịch sử hỏi AI, bookmarks,
              flashcard reviews, kiểm tra, xác nhận nội quy của NV sẽ bị xoá theo (FK cascade).
              Audit log vẫn giữ (actor được set NULL).
            </p>
          </div>
        </div>

        <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-sm">
          <div><span className="text-slate-500">NV:</span> <strong>{employee.full_name}</strong></div>
          <div><span className="text-slate-500">ID:</span> <span className="font-mono">#{employee.id}</span></div>
          <div><span className="text-slate-500">SĐT:</span> {employee.phone || <span className="text-slate-400">—</span>}</div>
          <div><span className="text-slate-500">Shop:</span> {employee.shop_id}</div>
          <div><span className="text-slate-500">Status:</span> <span className="font-mono">{employee.status}</span></div>
        </div>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700">
            Nhập lại chính xác họ tên NV để xác nhận:
          </span>
          <input
            value={nameConfirm}
            onChange={(e) => setNameConfirm(e.target.value)}
            placeholder={employee.full_name}
            autoFocus
            className={`mt-1 w-full px-3 py-2 rounded-lg border outline-none ${
              nameConfirm.length === 0
                ? 'border-slate-300'
                : nameMatch
                  ? 'border-emerald-400 focus:ring-2 focus:ring-emerald-100'
                  : 'border-rose-400 focus:ring-2 focus:ring-rose-100'
            }`}
            data-testid="delete-name-confirm"
          />
          <span className={`text-xs mt-1 block ${nameMatch ? 'text-emerald-600' : 'text-slate-400'}`}>
            {nameMatch ? '✓ Khớp' : 'Phải khớp chính xác (có dấu, hoa/thường)'}
          </span>
        </label>

        <label className="block">
          <span className="block text-sm font-medium text-slate-700">Mã TOTP L1 (6 chữ số) của admin:</span>
          <input
            value={totp}
            onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            placeholder="123456"
            maxLength={6}
            className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 font-mono tracking-widest text-center text-lg"
            data-testid="delete-totp"
          />
        </label>

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={onClose} className="flex-1 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-medium">
            Huỷ
          </button>
          <button
            type="submit"
            disabled={busy || !nameMatch || !totpOk}
            className="flex-1 py-2 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="delete-confirm-submit"
          >
            {busy ? 'Đang xoá…' : 'Xoá vĩnh viễn'}
          </button>
        </div>
      </form>
    </div>
  );
}
